import { MESSAGE_CATALOGS } from './catalog';
import { isMessageKey, type MessageKey } from './message-keys';
import { SUPPORTED_LOCALES, type AppLocale, type MessageParameters, type MessageTemplate } from './types';

export interface I18nPort {
  readonly locale: AppLocale;
  t(key: MessageKey, parameters?: MessageParameters): string;
  text(key: string, parameters?: MessageParameters): string;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  setLocale(locale: AppLocale): void;
  subscribe(listener: () => void): () => void;
  applyDocument(root?: ParentNode): void;
}

const aliases: Readonly<Record<string, AppLocale>> = {
  en: 'en', ru: 'ru', es: 'es', zh: 'zh-Hans', 'zh-cn': 'zh-Hans', 'zh-sg': 'zh-Hans', 'zh-hans': 'zh-Hans',
};

export function normalizeLocale(value: unknown): AppLocale | null {
  if (typeof value !== 'string') return null;
  if ((SUPPORTED_LOCALES as readonly string[]).includes(value)) return value as AppLocale;
  const normalized = value.trim().toLowerCase();
  return aliases[normalized] ?? aliases[normalized.split('-')[0] ?? ''] ?? null;
}

export function resolveLocale(explicit: unknown, browserLocales: readonly string[] = []): AppLocale {
  const stored = normalizeLocale(explicit);
  if (stored) return stored;
  for (const candidate of browserLocales) {
    const locale = normalizeLocale(candidate);
    if (locale) return locale;
  }
  return 'en';
}

function interpolate(template: string, parameters: MessageParameters): string {
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key: string) => String(parameters[key] ?? `{${key}}`));
}

export class I18nService implements I18nPort {
  private readonly listeners = new Set<() => void>();
  private pluralRules: Intl.PluralRules;
  private numberFormatters = new Map<string, Intl.NumberFormat>();

  constructor(private currentLocale: AppLocale) {
    this.pluralRules = new Intl.PluralRules(currentLocale);
    this.syncDocumentLanguage();
  }

  get locale(): AppLocale { return this.currentLocale; }

  t(key: MessageKey, parameters: MessageParameters = {}): string {
    const value: MessageTemplate = MESSAGE_CATALOGS[this.currentLocale][key] ?? MESSAGE_CATALOGS.en[key];
    const template = typeof value === 'string'
      ? value
      : value[this.pluralRules.select(Number(parameters.count ?? 0))] ?? value.other;
    return interpolate(template, parameters);
  }

  text(key: string, parameters: MessageParameters = {}): string {
    return isMessageKey(key) ? this.t(key, parameters) : key;
  }

  formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
    const cacheKey = JSON.stringify(options);
    let formatter = this.numberFormatters.get(cacheKey);
    if (!formatter) {
      formatter = new Intl.NumberFormat(this.currentLocale, options);
      this.numberFormatters.set(cacheKey, formatter);
    }
    return formatter.format(value);
  }

  setLocale(locale: AppLocale): void {
    if (locale === this.currentLocale) return;
    this.currentLocale = locale;
    this.pluralRules = new Intl.PluralRules(locale);
    this.numberFormatters.clear();
    this.syncDocumentLanguage();
    for (const listener of this.listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  applyDocument(root: ParentNode = document): void {
    for (const element of root.querySelectorAll<HTMLElement>('[data-i18n]')) {
      const key = element.dataset.i18n;
      if (key) element.textContent = this.text(key);
    }
    for (const element of root.querySelectorAll<HTMLElement>('[data-i18n-aria]')) {
      const key = element.dataset.i18nAria;
      if (key) element.setAttribute('aria-label', this.text(key));
    }
    for (const element of root.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder]')) {
      const key = element.dataset.i18nPlaceholder;
      if (key) element.placeholder = this.text(key);
    }
    for (const element of root.querySelectorAll<HTMLElement>('[data-i18n-alt]')) {
      const key = element.dataset.i18nAlt;
      if (key) element.setAttribute('alt', this.text(key));
    }
  }

  private syncDocumentLanguage(): void {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = this.currentLocale;
    document.documentElement.dir = 'ltr';
  }
}
